/**
 * Chat Stream Utilities
 *
 * Type guards, callback types, and helper functions for chat stream events.
 */
import type {
  ChatStreamEvent,
  ChatStreamThinkingEvent,
  ChatStreamFinalResponseEvent,
  ChatStreamEndEvent,
} from '@contracts';

// ============================================================================
// Callback Types
// ============================================================================

/**
 * Callback for thinking progress updates.
 */
export type OnThinkingCallback = (event: ChatStreamThinkingEvent) => void;

/**
 * Callback for final response.
 */
export type OnFinalResponseCallback = (event: ChatStreamFinalResponseEvent) => void;

/**
 * Callback for stream completion.
 */
export type OnStreamEndCallback = (event: ChatStreamEndEvent) => void;

/**
 * Callback for stream errors.
 */
export type OnStreamErrorCallback = (error: Error) => void;

/**
 * All callbacks for handling chat stream events.
 */
export interface ChatStreamCallbacks {
  onThinking?: OnThinkingCallback;
  onFinalResponse: OnFinalResponseCallback;
  onStreamEnd?: OnStreamEndCallback;
  onError: OnStreamErrorCallback;
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if an event is a THINKING event.
 */
export function isThinkingEvent(event: ChatStreamEvent): event is ChatStreamThinkingEvent {
  return event.eventType === 'THINKING';
}

/**
 * Type guard to check if an event is a FINAL_RESPONSE event.
 */
export function isFinalResponseEvent(
  event: ChatStreamEvent,
): event is ChatStreamFinalResponseEvent {
  return event.eventType === 'FINAL_RESPONSE';
}

/**
 * Type guard to check if an event is a STREAM_END event.
 */
export function isStreamEndEvent(event: ChatStreamEvent): event is ChatStreamEndEvent {
  return event.eventType === 'STREAM_END';
}
